import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Skeleton } from "../../../ui/skeleton";
import { AiDraftPanel, DraftSeed } from "../components/calendar/AiDraftPanel";
import { Sparkles, Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { useToast } from "../../../../hooks/use-toast";

type Event = {
  id: string;
  name: string | null;
  region: string | null;
  event_date: string | null;
  description: string | null;
  applicable_types: string[] | null;
};

type Idea = { title: string; description: string; angle: string };

const eventEmoji = (name: string | null) => {
  const n = (name || "").toLowerCase();
  if (n.includes("christmas")) return "🎄";
  if (n.includes("new year")) return "🎉";
  if (n.includes("chinese new year") || n.includes("cny")) return "🧧";
  if (n.includes("hari raya") || n.includes("eid")) return "🌙";
  if (n.includes("deepavali") || n.includes("diwali")) return "🪔";
  if (n.includes("national day")) return "🇸🇬";
  if (n.includes("mother")) return "💐";
  if (n.includes("father")) return "👔";
  if (n.includes("valentine")) return "❤️";
  if (n.includes("mid-autumn") || n.includes("mooncake")) return "🥮";
  if (n.includes("black friday")) return "🛍️";
  return "📅";
};

const daysUntil = (dateStr: string) => {
  const d = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diff = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
};

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-SG", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

const monthName = () => new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });

export default function Calendar() {
  const { merchant } = useMerchant();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [region, setRegion] = useState<"all" | "Singapore" | "Global">("all");
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const [ideas, setIdeas] = useState<Idea[] | null>(null);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [ideasError, setIdeasError] = useState<string | null>(null);

  const [draftSeed, setDraftSeed] = useState<DraftSeed | null>(null);

  useEffect(() => {
    const load = async () => {
      setEventsLoading(true);
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await epicengageSupabasev1
        .from("campaign_events")
        .select("id, name, region, event_date, description, applicable_types")
        .gte("event_date", today)
        .eq("is_active", true)
        .order("event_date", { ascending: true });
      if (error) {
        toast({ title: "Failed to load events", description: error.message, variant: "destructive" });
      }
      setEvents(data || []);
      setEventsLoading(false);
    };
    load();
  }, [toast]);

  const filteredEvents = useMemo(
    () => (region === "all" ? events : events.filter((e) => e.region === region)),
    [events, region]
  );

  const loadIdeas = async () => {
    if (!merchant) return;
    setIdeasLoading(true);
    setIdeasError(null);
    setIdeas(null);
    try {
      const { data: sessionData } = await epicengageSupabasev1.auth.getSession();
      const token = sessionData.session?.access_token;
      const url = `${process.env.REACT_APP_SUPABASE_ENGAGEV1_URL}/functions/v1/ai-campaign-draft`;
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mode: "ideas",
          business_type: merchant.business_type || "small business",
          brand_tone: merchant.brand_tone || "Friendly & Casual",
          month: monthName(),
        }),
      });
      if (!resp.ok || !resp.body) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let assembled = "";
      let done = false;
      while (!done) {
        const { done: d, value } = await reader.read();
        if (d) break;
        textBuffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, idx);
          textBuffer = textBuffer.slice(idx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") {
            done = true;
            break;
          }
          try {
            const parsed = JSON.parse(json);
            const c = parsed.choices?.[0]?.delta?.content;
            if (c) assembled += c;
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
      // Strip optional code fences
      const cleaned = assembled.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(cleaned);
      setIdeas(parsed.ideas || []);
    } catch (e) {
      setIdeasError(e instanceof Error ? e.message : "Failed to load ideas");
    } finally {
      setIdeasLoading(false);
    }
  };

  return (
    <div>
      <div className="px-10 py-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Campaign Calendar</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Upcoming events and AI-generated campaign ideas
            </p>
          </div>
        </div>

        <div className="mt-6">
          <Tabs value={region} onValueChange={(v) => setRegion(v as typeof region)}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="Singapore">Singapore</TabsTrigger>
              <TabsTrigger value="Global">Global</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <Tabs defaultValue="events" className="mt-6" onValueChange={(v) => {
          if (v === "ideas" && !ideas && !ideasLoading) loadIdeas();
        }}>
          <TabsList>
            <TabsTrigger value="events">
              <CalendarIcon className="h-4 w-4 mr-2" /> Holiday Events
            </TabsTrigger>
            <TabsTrigger value="ideas">
              <Sparkles className="h-4 w-4 mr-2" /> Monthly Ideas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="mt-6">
            {eventsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-44 w-full" />
                ))}
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="rounded-lg border border-dashed p-12 text-center text-sm text-muted-foreground">
                No upcoming events for this region.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredEvents.map((ev) => {
                  const days = ev.event_date ? daysUntil(ev.event_date) : 0;
                  const soon = days >= 0 && days <= 30;
                  return (
                    <Card key={ev.id} className="flex flex-col">
                      <CardHeader>
                        <div className="flex items-start justify-between gap-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <span className="text-2xl">{eventEmoji(ev.name)}</span>
                            <span>{ev.name}</span>
                          </CardTitle>
                          {soon && (
                            <Badge variant="outline" className="border-amber-400/50 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                              Coming soon
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {ev.event_date && formatDate(ev.event_date)} · {days === 0 ? "Today" : `${days} days away`}
                        </div>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col">
                        {ev.description && (
                          <p className="text-sm text-muted-foreground">{ev.description}</p>
                        )}
                        {ev.applicable_types && ev.applicable_types.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {ev.applicable_types.map((t) => (
                              <Badge key={t} variant="secondary" className="text-xs">
                                {t}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <div className="mt-4 pt-4 border-t flex justify-end">
                          <Button
                            size="sm"
                            onClick={() =>
                              setDraftSeed({
                                mode: "event",
                                event_id: ev.id,
                                event_name: ev.name || "Campaign",
                                event_date: ev.event_date || "",
                              })
                            }
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Create Campaign
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="ideas" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                AI campaign ideas for <span className="font-medium text-foreground">{monthName()}</span>
              </p>
              <Button variant="outline" size="sm" onClick={loadIdeas} disabled={ideasLoading}>
                {ideasLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Regenerate
              </Button>
            </div>

            {ideasLoading && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-44 w-full" />
                ))}
              </div>
            )}

            {ideasError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                {ideasError}
              </div>
            )}

            {!ideasLoading && !ideasError && ideas && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ideas.map((idea, i) => (
                  <Card key={i} className="flex flex-col">
                    <CardHeader>
                      <CardTitle className="text-base">{idea.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <p className="text-sm text-muted-foreground">{idea.description}</p>
                      {idea.angle && (
                        <p className="text-xs text-muted-foreground mt-2 italic">Angle: {idea.angle}</p>
                      )}
                      <div className="mt-4 pt-4 border-t flex justify-end">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            setDraftSeed({
                              mode: "event",
                              event_id: null,
                              event_name: idea.title,
                              event_date: new Date().toISOString().slice(0, 10),
                              extra_context: idea.description,
                            })
                          }
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          Use this idea
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!ideasLoading && !ideasError && !ideas && (
              <div className="rounded-lg border border-dashed p-12 text-center text-sm text-muted-foreground">
                Click "Regenerate" to load AI campaign ideas for {monthName()}.
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <AiDraftPanel
        seed={draftSeed}
        onClose={() => setDraftSeed(null)}
        onSent={(templateId) => {
          setDraftSeed(null);
          navigate(`/clientpanel/enagev1/campaigns?template=${templateId}`);
        }}
      />
    </div>
  );
}
